import React from 'react';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Skeleton from '@mui/material/Skeleton';

import GenericList from '@ts-core/core/components/GenericList';
import QuestionPreviewContainer from '@ts-core/modules/exercises/components/QuestionPreviewContainer';
import QuestionScore from '@ts-core/modules/exercises/components/QuestionScore';
import type { QuestionAnswerType } from '@ts-core/modules/exercises/types';

import { CoreMultipleChoiceQuestion, CoreMultipleChoiceQuestionOption } from '../../types';

interface MultipleChoiceQuestionContainerProps<TQuestion extends CoreMultipleChoiceQuestion> {
  question?: TQuestion;
  loading?: boolean;
  selectedOptionType?: QuestionAnswerType;
  selectedOption?: string;
  score?: number;

  renderOption: (index: number, option?: CoreMultipleChoiceQuestionOption) => React.ReactNode;
  renderNoAnswerOption?: () => React.ReactNode;
  onChangeOption?: React.ChangeEventHandler;

  noColors?: boolean;
  hideScores?: boolean;
}

const MultipleChoiceQuestionContainer = <TQuestion extends CoreMultipleChoiceQuestion>(props: MultipleChoiceQuestionContainerProps<TQuestion>) => {
  const {
    question,
    score,
    loading = false,
    hideScores = false,
    noColors = false,
    selectedOption,
    selectedOptionType,
    renderOption,
    renderNoAnswerOption,
    onChangeOption,
  } = props;

  return (
    <QuestionPreviewContainer optionType={selectedOptionType} noColors={noColors}>
      <FormControl component="fieldset" sx={{ mt: 2 }}>
        {(question?.text || loading) && (
          <FormLabel component="legend" sx={{ mb: 2, lineHeight: 1.4 }}>
            {loading ? <Skeleton width={80} /> : question?.text}
          </FormLabel>
        )}
        <RadioGroup aria-label={question?.text} value={selectedOption || 'none'} onChange={onChangeOption}>
          <GenericList items={question?.options} loading={loading} skeletonsCount={3} renderItem={renderOption} />
          {!loading && renderNoAnswerOption?.()}
        </RadioGroup>
      </FormControl>
      {!hideScores && <QuestionScore score={score} />}
    </QuestionPreviewContainer>
  );
};

export default MultipleChoiceQuestionContainer;
